import React, { Component } from 'react'
import { getInformationWithDataV3 } from '../../services/CABE';
import { NumberFormat } from '../../functions/generalFunctions'
import { pricesFormat } from '../../functions/pricesFormat'
import '../../css/dashboard.css'
import LoadingWindow from '../../components/LoadingWindow';
import { AiOutlineReload, AiOutlineDollarCircle } from "react-icons/ai";
import { Menu } from "../../components/Menu/Menu";

function formatDateInput(date) {
    return date.toISOString().slice(0, 10)
}
function addDays(date, days) {
    const d = new Date(date)
    d.setDate(d.getDate() + days)
    return d
}

/**
 * CommissionsV3 — new report (2026-08-18), not a clone of any v2 page.
 * Distinct from the existing SalesCommissionReport.js (v2, untouched — that
 * one calculates $ commission from revenue/cash receipts differently). This
 * report answers the client's own question — "cuantas SO se hicieron
 * invoices" AND, since 2026-08-27, the actual $ commission earned per
 * salesperson — using document_audit_trail's salespersonNo (captured at SO
 * creation, immune to the later-edit-overwrite bug) instead of the mutable
 * sales_orders_aws_v2.salesperson_no column.
 *
 * "Converted to Invoice" and "Total Invoiced"/"Commission" (2026-08-27) are
 * all detected via the same MAS90-level join Price Change Report V3 and
 * Traceability V3 use (AR_InvoiceHistoryHeader.SalesOrderNo = the SO's own
 * order number) — NOT dependent on Invoice V3 writing its own audit event
 * (it doesn't yet, a separate team's call, deliberately untouched here), so
 * this reflects real invoicing activity immediately. Commission is a flat
 * 5% of real invoiced revenue, fixed for now — see getCommissions's own
 * comment in documentAuditTrail.ts.
 *
 * Every active company is loaded and shown together, grouped with a
 * separator per company (2026-08-27, client's ask) — no company selector,
 * see loadData()/companySections below.
 */
export default class CommissionsV3 extends Component {
    constructor(props) {
        super(props)
        this.ModalLoading = React.createRef();
    }

    state = {
        dateStart: formatDateInput(addDays(new Date(), -30)),
        dateEnd: formatDateInput(new Date()),
        salespersonNo: '',
        companies: [],
        // Flat, each row carries its own idCompany/companyName so render can
        // group by company client-side (2026-08-27, client's ask — "todas
        // las empresas... con un separador o agrupación como la hemos
        // manejado") — same flat-rows-grouped-at-render pattern as
        // PriceChangeReportV3.js's linesByInvoice / TraceabilityV3.js. Before
        // this, the page silently used whichever company the session cookie
        // happened to be on, with no way to see more than one at a time.
        rows: [],
        loaded: false,
        loadError: '',
        // Server-computed rate (2026-08-27) — default matches the backend's
        // own fallback until the first real response confirms it.
        commissionRate: 0.05,
    }

    componentDidMount() {
        this.fetchCompanies()
    }

    async fetchCompanies() {
        try {
            const resp = await getInformationWithDataV3('/v3/pricing/companies', {})
            if (resp && resp.status && resp.status.code === 1 && Array.isArray(resp.data)) {
                this.setState({ companies: resp.data })
            }
        } catch (error) {
            console.error('fetchCompanies failed:', error)
        }
    }

    onChangeField = (field, value) => {
        this.setState({ [field]: value })
    }

    /** Loads every active company's commissions in parallel and flattens them into one row list. */
    async loadData() {
        this.ModalLoading.current.showState(true)
        this.setState({ loadError: '' })
        try {
            const companies = this.state.companies.length > 0 ? this.state.companies : await (async () => {
                await this.fetchCompanies()
                return this.state.companies
            })()
            const responses = await Promise.all(companies.map((company) =>
                getInformationWithDataV3('/v3/audit/commissions', {
                    idCompany: company.idCompany,
                    dateStart: this.state.dateStart,
                    dateEnd: this.state.dateEnd,
                    salespersonNo: this.state.salespersonNo || undefined,
                })
            ))
            const allRows = []
            let anyFailed = false
            let commissionRate = this.state.commissionRate
            responses.forEach((resp, i) => {
                if (resp && resp.status && resp.status.code === 1) {
                    for (const row of (resp.data || [])) {
                        allRows.push({ ...row, idCompany: companies[i].idCompany, companyName: companies[i].name })
                    }
                    if (resp.commissionRate !== undefined) commissionRate = Number(resp.commissionRate)
                } else {
                    anyFailed = true
                }
            })
            this.setState({ rows: allRows, loaded: true, commissionRate, loadError: anyFailed ? 'Some companies could not be loaded.' : '' })
        } catch (error) {
            console.error('CommissionsV3 loadData failed:', error)
            this.setState({ rows: [], loaded: true, loadError: 'Could not load commissions data — the server did not respond.' })
        } finally {
            this.ModalLoading.current.showState(false)
        }
    }

    render() {
        const { rows, loaded, companies } = this.state
        const totalSO = rows.reduce((sum, r) => sum + Number(r.soCount || 0), 0)
        const totalConverted = rows.reduce((sum, r) => sum + Number(r.convertedCount || 0), 0)
        // Commission (2026-08-27, client's ask — "calcular las comisiones de
        // cada vendedor a partir de las invoices que ha vendido"): a flat 5%
        // of real invoiced revenue, computed server-side (getCommissions) —
        // fixed rate for now, "en lo que me dan mas reglas" (client's own
        // words, pending real per-product/tier commission rules).
        const totalInvoiced = rows.reduce((sum, r) => sum + Number(r.totalInvoiced || 0), 0)
        const totalCommission = rows.reduce((sum, r) => sum + Number(r.commission || 0), 0)
        const commissionRatePct = Math.round(this.state.commissionRate * 1000) / 10

        // Group by company, in the SAME order `companies` came back in
        // (2026-08-27, client's ask — "todas las empresas... con un
        // separador o agrupación como la hemos manejado") — same
        // flat-rows-grouped-at-render pattern as PriceChangeReportV3.js.
        const rowsByCompany = {}
        for (const r of rows) {
            const key = r.idCompany
            if (!rowsByCompany[key]) rowsByCompany[key] = []
            rowsByCompany[key].push(r)
        }
        const companySections = companies
            .map((c) => ({ company: c, companyRows: rowsByCompany[c.idCompany] || [] }))
            .filter((s) => s.companyRows.length > 0)

        return (
            <div className="dash-page pb-5">
                <Menu Refresh={this.state.Refresh} Father={this} />
                <LoadingWindow ref={this.ModalLoading} />

                <div style={{ marginTop: 30, background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #2563EB 100%)', padding: '2rem 2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <div style={{ background: 'rgba(255,255,255,0.12)', borderRadius: '12px', padding: '0.75rem', display: 'flex' }}>
                            <AiOutlineDollarCircle style={{ color: 'white', fontSize: '1.75rem' }} />
                        </div>
                        <div>
                            <h1 style={{ color: 'white', fontWeight: 800, fontSize: '1.375rem', margin: 0 }}>Commissions (V3)</h1>
                            <p style={{ color: 'rgba(255,255,255,0.6)', margin: 0, fontSize: '0.8rem' }}>Sales Orders created, converted to Invoice, and commission earned, by salesperson</p>
                        </div>
                    </div>
                </div>

                <div className="container-fluid" style={{ marginTop: 24 }}>
                    {this.state.loadError && (
                        <div className="metric-card status-danger mb-3">
                            <div style={{ color: '#DC2626', fontSize: '0.85rem' }}>{this.state.loadError}</div>
                        </div>
                    )}

                    <div className="row g-3 mb-4 align-items-end justify-content-center">
                        <div className="col-auto">
                            <label style={{ display: 'block', fontSize: '0.78rem', color: '#64748B', marginBottom: '4px' }}>From</label>
                            <input type="date" value={this.state.dateStart} onChange={(e) => this.onChangeField('dateStart', e.target.value)}
                                style={{ padding: '0.6rem 0.85rem', border: '1.5px solid #E2E8F0', borderRadius: '10px', fontSize: '0.9rem' }} />
                        </div>
                        <div className="col-auto">
                            <label style={{ display: 'block', fontSize: '0.78rem', color: '#64748B', marginBottom: '4px' }}>To</label>
                            <input type="date" value={this.state.dateEnd} onChange={(e) => this.onChangeField('dateEnd', e.target.value)}
                                style={{ padding: '0.6rem 0.85rem', border: '1.5px solid #E2E8F0', borderRadius: '10px', fontSize: '0.9rem' }} />
                        </div>
                        <div className="col-auto">
                            <label style={{ display: 'block', fontSize: '0.78rem', color: '#64748B', marginBottom: '4px' }}>Salesperson No. (optional)</label>
                            <input type="text" placeholder="e.g. JC" value={this.state.salespersonNo}
                                onChange={(e) => this.onChangeField('salespersonNo', e.target.value.toUpperCase())}
                                style={{ padding: '0.6rem 0.85rem', border: '1.5px solid #E2E8F0', borderRadius: '10px', fontSize: '0.9rem', width: '160px' }} />
                        </div>
                        <div className="col-auto">
                            <button
                                onClick={() => this.loadData()}
                                type="button"
                                style={{
                                    background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 100%)',
                                    border: 'none', borderRadius: '10px', color: 'white', padding: '0.7rem 1.5rem',
                                    fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer', display: 'flex',
                                    alignItems: 'center', gap: '0.5rem',
                                }}
                            >
                                <AiOutlineReload /> Load Data
                            </button>
                        </div>
                    </div>

                    {loaded && rows.length > 0 && (
                        <>
                            <div className="row g-3 mb-4">
                                <div className="col-md-3">
                                    <div className="metric-card status-primary">
                                        <div className="metric-label">Salespeople with activity</div>
                                        <div className="metric-value">{rows.length}</div>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="metric-card status-success">
                                        <div className="metric-label">Total Sales Orders created</div>
                                        <div className="metric-value">{totalSO}</div>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="metric-card status-warning">
                                        <div className="metric-label">Total converted to Invoice</div>
                                        <div className="metric-value">{totalConverted}</div>
                                    </div>
                                </div>
                                {/* Commission (2026-08-27, client's ask) — flat rate over real
                                    invoiced revenue, see getCommissions's own comment for why
                                    it's fixed at 5% for now. */}
                                <div className="col-md-3">
                                    <div className="metric-card status-primary">
                                        <div className="metric-label">Total Commission ({commissionRatePct}%)</div>
                                        <div className="metric-value">${NumberFormat(pricesFormat(totalCommission))}</div>
                                    </div>
                                </div>
                            </div>

                            <div className="tableFixHeadSmall">
                                <table className="table table-clean align-middle">
                                    <thead>
                                        <tr>
                                            <th>Salesperson No.</th>
                                            <th className="text-end">Sales Orders Created</th>
                                            <th className="text-end">Converted to Invoice</th>
                                            <th className="text-center">Conversion Rate</th>
                                            <th className="text-end">Total Invoiced</th>
                                            <th className="text-end">Commission ({commissionRatePct}%)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {companySections.map(({ company, companyRows }) => (
                                            <React.Fragment key={company.idCompany}>
                                                {/* Company group separator (2026-08-27, client's ask —
                                                    "todas las empresas... con un separador o
                                                    agrupación como la hemos manejado") — same solid
                                                    brand-blue tint + thick top border used to separate
                                                    invoice groups in PriceChangeReportV3.js. */}
                                                <tr style={{ background: '#DBEAFE', borderTop: '3px solid #2563EB' }}>
                                                    <td colSpan={6} style={{ padding: '10px 16px', borderLeft: '4px solid #2563EB', fontWeight: 800, fontSize: '0.8rem', color: '#1e3a8a' }}>
                                                        {company.name}
                                                    </td>
                                                </tr>
                                                {companyRows.map((row, i) => {
                                                    const so = Number(row.soCount || 0)
                                                    const conv = Number(row.convertedCount || 0)
                                                    const rate = so > 0 ? (conv / so) * 100 : 0
                                                    const invoiced = Number(row.totalInvoiced || 0)
                                                    const commission = Number(row.commission || 0)
                                                    return (
                                                        <tr key={company.idCompany + '_' + i}>
                                                            <td>{row.salespersonNo || '(unassigned)'}</td>
                                                            <td className="text-end">{so}</td>
                                                            <td className="text-end">{conv}</td>
                                                            <td className="text-center">
                                                                <span style={{
                                                                    display: 'inline-block', padding: '2px 10px', borderRadius: '999px',
                                                                    fontSize: '0.78rem', fontWeight: 600,
                                                                    background: rate >= 80 ? 'rgba(16,185,129,0.1)' : rate > 0 ? 'rgba(245,158,11,0.1)' : 'rgba(100,116,139,0.08)',
                                                                    color: rate >= 80 ? '#059669' : rate > 0 ? '#D97706' : '#64748B',
                                                                }}>{rate.toFixed(1)}%</span>
                                                            </td>
                                                            <td className="text-end">${NumberFormat(pricesFormat(invoiced))}</td>
                                                            <td className="text-end" style={{ fontWeight: 700, color: '#065F46' }}>${NumberFormat(pricesFormat(commission))}</td>
                                                        </tr>
                                                    )
                                                })}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}

                    {loaded && rows.length === 0 && !this.state.loadError && (
                        <div style={{ textAlign: 'center', padding: '4rem 2rem', color: '#94A3B8' }}>
                            <AiOutlineDollarCircle style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.4 }} />
                            <p style={{ margin: 0, fontSize: '1rem' }}>No Sales Orders found in this date range.</p>
                        </div>
                    )}

                    {!loaded && (
                        <div style={{ textAlign: 'center', padding: '4rem 2rem', color: '#94A3B8' }}>
                            <AiOutlineReload style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.4 }} />
                            <p style={{ margin: 0, fontSize: '1rem' }}>Click <strong style={{ color: '#1e3a8a' }}>Load Data</strong> to see commission counts.</p>
                        </div>
                    )}
                </div>
            </div>
        );
    }
}
